import { Car, LayoutDashboard, Clock, FileText, CalendarDays, CalendarOff, Hourglass, Banknote, Briefcase, BarChart3, ClipboardList, Package, Wrench, HandMetal, ClipboardCheck, FolderOpen, Inbox, Send, ListChecks, FileCheck, Building2, Receipt, Users, History, Tags, Mail, Settings, Shield, CheckSquare, ShoppingCart, Rss, Plug, Bell, LayoutTemplate, type LucideIcon, MailOpen, Shapes, ListOrdered } from 'lucide-react'
import type { User } from '../types'
import { can, type Capability } from '../utils/permissions'

/**
 * JEDINÁ definice navigace aplikace (boční menu AppLayout, titulek stránky v mobilní liště,
 * kontrola úvodní stránky). Položky a jejich capability odpovídají route guardům v App.tsx —
 * při přidání stránky ji doplň sem i tam.
 */

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Aktivní jen při přesné shodě cesty (jinak i pro podstránky, např. /attendance/orders/123). */
  end?: boolean
  /** Další cesty, při kterých je položka aktivní (např. detail nářadí /tools/:id). */
  activePattern?: RegExp
  /** Zobrazit jen se schopností; u pole stačí kterákoli. */
  capability?: Capability | Capability[]
  /** Počet vedle popisku (vykresluje NavBadge), např. žádosti o nepřítomnost ke schválení. */
  badge?: 'absencesPending'
}

export interface NavSection {
  id: string
  label: string
  icon: LucideIcon
  /** Schopnost modulu (jako dřívější dlaždice rozcestníku); u pole stačí kterákoli. */
  capability?: Capability | Capability[]
  items: NavItem[]
}

/**
 * Kdo vidí Přehled (dashboard): vedení, manažeři i pracovníci (vlastní docházka) — obsah se
 * přizpůsobí oprávněním (Dashboard.tsx → dashboardMode).
 */
export const DASHBOARD_CAPABILITIES: Capability[] = [
  'attendance.own',
  'attendance.planning',
  'attendance.absenceApprove',
  'attendance.ordersManage',
  'attendance.overtimeView',
  'reports.view',
  'tasks.viewAll',
  'preparations.fullManage',
  'tools.manage',
]

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    label: 'Přehled',
    icon: LayoutDashboard,
    capability: DASHBOARD_CAPABILITIES,
    items: [{ to: '/dashboard', label: 'Přehled', icon: LayoutDashboard, end: true }],
  },
  {
    id: 'attendance',
    label: 'Docházka',
    icon: Clock,
    capability: 'module.attendance',
    items: [
      { to: '/attendance', label: 'Moje hodiny', icon: Clock, end: true },
      { to: '/attendance/report', label: 'Můj report', icon: FileText },
      { to: '/attendance/manage', label: 'Plánování', icon: CalendarDays, capability: 'attendance.planning' },
      // Jedna položka pro schvalování i přehled (záložky na stránce); odznak = počet žádostí ke schválení.
      { to: '/attendance/absences/approval', label: 'Nepřítomnosti', icon: CalendarOff, activePattern: /^\/attendance\/absences(\/|$)/, capability: ['attendance.absenceApprove', 'attendance.absenceManageAll'], badge: 'absencesPending' },
      { to: '/attendance/overtime', label: 'Přesčasy', icon: Hourglass, capability: 'attendance.overtimeView' },
      { to: '/attendance/settings', label: 'Nastavení docházky', icon: Settings, capability: ['admin.settings', 'admin.absenceSettings', 'attendance.ordersManage', 'attendance.overtimeManage'] },
    ],
  },
  {
    id: 'orders',
    label: 'Zakázky',
    icon: Briefcase,
    capability: 'module.attendance',
    items: [
      { to: '/attendance/orders', label: 'Zakázky', icon: ShoppingCart, capability: 'attendance.ordersManage' },
    ],
  },
  {
    id: 'preparations',
    label: 'Přípravy a vratky',
    icon: ClipboardList,
    capability: 'module.preparations',
    items: [
      { to: '/preparations', label: 'Přípravy a vratky', icon: ClipboardList, end: true, activePattern: /^\/preparations\/(?!statuses$)[^/]+/ },
      { to: '/products', label: 'Produkty', icon: Package },
      { to: '/preparations/statuses', label: 'Stavy příprav', icon: Tags, capability: 'admin.statuses' },
    ],
  },
  {
    id: 'tools',
    label: 'Půjčovna nářadí',
    icon: Wrench,
    capability: 'module.tools',
    items: [
      { to: '/tools', label: 'Nářadí', icon: Wrench, end: true, activePattern: /^\/tools\/(?!my-borrowings|inventory|categories)[^/]+/ },
      { to: '/tools/my-borrowings', label: 'Moje zapůjčení', icon: HandMetal },
      { to: '/tools/inventory', label: 'Inventura', icon: ClipboardCheck, capability: 'tools.manage' },
      { to: '/tools/categories', label: 'Kategorie', icon: FolderOpen, capability: 'tools.manage' },
    ],
  },
  {
    id: 'vehicles',
    label: 'Vozový park',
    icon: Car,
    capability: 'module.vehicles',
    items: [{ to: '/vehicles', label: 'Vozidla', icon: Car }],
  },
  {
    id: 'tasks',
    label: 'Úkoly',
    icon: CheckSquare,
    capability: 'module.tasks',
    items: [
      { to: '/tasks', label: 'Moje úkoly', icon: Inbox, end: true, activePattern: /^\/tasks\/detail\// },
      { to: '/tasks/created-by-me', label: 'Zadané mnou', icon: Send },
      { to: '/tasks/all', label: 'Všechny úkoly', icon: ListChecks, capability: 'tasks.viewAll' },
      { to: '/tasks/statuses', label: 'Stavy úkolů', icon: Tags, capability: 'tasks.manageStatuses' },
    ],
  },
  {
    id: 'inbox',
    label: 'Doklady z e-mailu',
    icon: MailOpen,
    capability: 'module.inbox',
    items: [
      { to: '/inbox', label: 'Doklady', icon: MailOpen, end: true, activePattern: /^\/inbox\/(?!settings)[^/]+$/ },
      { to: '/inbox/settings', label: 'Schránka a spotřeba', icon: Settings },
    ],
  },
  {
    id: 'revisions',
    label: 'Revize',
    icon: FileCheck,
    capability: 'module.revisions',
    items: [{ to: '/revisions', label: 'Revizní zprávy', icon: FileCheck }],
  },
  {
    id: 'reports',
    label: 'Reporty',
    icon: BarChart3,
    // Hodiny a mzdy patří do Reportů, ale řídí se vlastními schopnostmi — sekce se ukáže i bez module.reports
    // (a i když jsou Reporty pro majitele skryté); ostatní položky dál hlídá reports.view.
    capability: ['module.reports', 'attendance.payroll', 'attendance.orderReport'],
    items: [
      { to: '/reports', label: 'Vyhodnocení zakázek', icon: BarChart3, end: true, capability: 'reports.view' },
      { to: '/reports/companies', label: 'Vyhodnocení firem', icon: Building2, capability: 'reports.view' },
      { to: '/reports/invoices', label: 'Faktury', icon: Receipt, capability: 'reports.view' },
      { to: '/reports/hours', label: 'Hodiny a mzdy', icon: Banknote, capability: ['attendance.payroll', 'attendance.orderReport'] },
    ],
  },
  {
    id: 'admin',
    label: 'Administrace',
    icon: Settings,
    capability: 'module.admin',
    items: [
      { to: '/admin/users', label: 'Uživatelé', icon: Users, capability: 'admin.users' },
      { to: '/admin/role-permissions', label: 'Role a oprávnění', icon: Shield, capability: 'admin.rolesPermissions' },
      { to: '/admin/company', label: 'Nastavení firmy', icon: Building2, capability: 'admin.settings' },
      { to: '/admin/number-series', label: 'Číselné řady', icon: ListOrdered, capability: 'admin.settings' },
      { to: '/admin/order-types', label: 'Typy zakázek', icon: Shapes, capability: 'admin.settings' },
      { to: '/admin/feeds', label: 'Feedy', icon: Rss, capability: 'admin.settings' },
      { to: '/admin/integrations', label: 'Integrace', icon: Plug, capability: 'admin.settings' },
      { to: '/admin/email-templates', label: 'E-mailové šablony', icon: Mail, capability: 'admin.emailTemplates' },
      { to: '/document-visuals/editor', label: 'Vizuály dokumentů', icon: LayoutTemplate, capability: 'admin.settings' },
      { to: '/admin/notifications', label: 'Notifikace', icon: Bell, capability: 'admin.settings' },
      { to: '/admin/audit', label: 'Historie změn', icon: History, capability: 'admin.audit' },
    ],
  },
]

export function canAny(user: User | null | undefined, capability?: Capability | Capability[]): boolean {
  if (!capability) return true
  const caps = Array.isArray(capability) ? capability : [capability]
  return caps.some((c) => can(user, c))
}

/** Sekce a položky, které uživatel smí vidět (prázdné sekce vynechány). */
export function visibleNav(user: User | null | undefined): NavSection[] {
  return NAV_SECTIONS
    .filter((s) => canAny(user, s.capability))
    .map((s) => ({ ...s, items: s.items.filter((i) => canAny(user, i.capability)) }))
    .filter((s) => s.items.length > 0)
}

/** Je položka aktivní pro danou cestu? */
export function isItemActive(item: NavItem, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === item.to) return true
  if (item.activePattern?.test(path)) return true
  if (item.end) return false
  return path.startsWith(item.to + '/')
}

/** Aktivní položka pro cestu — nejdelší shoda (např. /attendance/orders před /attendance). */
export function findActiveItem(sections: NavSection[], pathname: string): { section: NavSection; item: NavItem } | null {
  let best: { section: NavSection; item: NavItem } | null = null
  for (const section of sections) {
    for (const item of section.items) {
      if (isItemActive(item, pathname) && (!best || item.to.length > best.item.to.length)) best = { section, item }
    }
  }
  return best
}

/**
 * Smí uživatel na cestu podle navigace? Cesty mimo navigaci (detaily, protokoly) se berou jako
 * dostupné — skutečnou kontrolu dělá route guard a backend.
 */
export function canAccessPath(user: User | null | undefined, path: string): boolean {
  let match: { section: NavSection; item: NavItem } | null = null
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (isItemActive(item, path) && (!match || item.to.length > match.item.to.length)) match = { section, item }
    }
  }
  if (!match) return true
  return canAny(user, match.section.capability) && canAny(user, match.item.capability)
}
